import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { category } from '../../items/models/category';
import { CategoryService } from '../../items/services/categories/category.service';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { AlertComponent } from '../../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../../shared/components/dialogs/confirm/confirm.component';
import { BreadcrumbsOptions } from '../../shared/models/breadcrumbs-model';
import { Page } from '../../shared/models/page.model';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { NewOrderItem } from '../models/newOrderItem';
import { OrderResponse } from '../models/orderResponse';
import { OrdersService } from '../services/orders/orders.service';

@Component({
  selector: 'app-new-order',
  standalone: true,
  imports: [
    FlexModule,
    TranslatePipe,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    MatIcon,
    MatTooltip,
    MatBadgeModule,
    BreadcrumbsComponent,
    MatSidenavModule,
    CommonModule,
    NgxMaskDirective
  ],
  providers: [
    provideNgxMask({dropSpecialCharacters: false}),
  ],
  templateUrl: './new-order.component.html',
  styleUrls: ['./new-order.component.scss']
})
export class NewOrderComponent implements OnInit, AfterViewInit {
  @ViewChild('drawer') drawer!: MatDrawer;
  private readonly PLACEHOLDER_IMAGE = 'assets/images/placeholder-image.png';
  public placeholderImage = this.PLACEHOLDER_IMAGE;
  counter: number = 0;
  cartItems: any[] = [];
  orderId: string | null = null;
  projectType: string = '';
  projectDescription: string = '';
  remarks: string = '';
  newOrderItems: NewOrderItem[] = [];
  filteredItems: NewOrderItem[] = [];
  breadcrumbsData: BreadcrumbsOptions = {
    title: 'Novo Pedido',
    buttons: [
      {
        label: 'Cancelar',
        style: 'red',
        do: () => {
          this.confirmDiscard();
        },
        disabled: false,
      },
      {
        label: 'Ver Carrinho',
        style: 'blue',
        do: () => {
          this.navigateToSummary();
        },
        disabled: true
      }
    ]
  };

  selectedCategoryId: number | null = null;

  categories: category[] = [];

  searchQuery: string = '';

  expandedKits: number[] = [];
  fromSummary: string | null;
  isEditingOrder: boolean = false;

  currentPage: number = 0;
  pageSize: number = 10;
  totalElements: number = 0;
  currentFilter: string = '';
  currentCategoryId: number | null = null;

  private filterTimeout: any;

  originalProjectType: string = '';
  originalProjectDescription: string = '';
  originalProjectCostCenter: string = '';
  originalRemarks: string = '';

  constructor(
    private route: ActivatedRoute,
    private ordersService: OrdersService,
    private categoryService: CategoryService,
    private router: Router,
    private translate: TranslateService,
    private dialog: MatDialog,
    private localStorageService: LocalStorageService,
  ) {
    this.orderId = this.route.snapshot.paramMap.get('id');
    this.projectType = this.route.snapshot.queryParamMap.get('projectType') || '';
    this.projectDescription = this.route.snapshot.queryParamMap.get('projectDescription') || '';
    this.remarks = this.route.snapshot.queryParamMap.get('remarks') || '';
    this.fromSummary = localStorage.getItem('fromSummary');
    if (this.orderId) {
      this.isEditingOrder = true;
      localStorage.setItem('isEditingOrder', 'true');
    }
  }

  ngOnInit(): void {
    this.loadCartFromLocalStorage();
    this.initData();
    this.updateBreadcrumbsLabel();
    this.loadFormValuesFromLocalStorage();
  }

  ngAfterViewInit(): void {
    if ((this.fromSummary === "true" || this.isEditingOrder) && this.drawer) {
      setTimeout(() => {
        this.drawer.open();
        localStorage.removeItem('fromSummary');
      }, 0);
    }
  }


  private initData(): void {
    this.loadCategories();
    if(this.orderId) {
      this.loadOrderDetails();
    } else {
      this.currentPage = 0;
      this.currentFilter = '';
      this.currentCategoryId = null;
      this.loadNewOrderItems();
    }
  }


  private loadCategories(): void {
    this.categoryService.listCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
      },
      error: (error) => {}
    });
  }

  private loadNewOrderItems(): void {
    this.ordersService.listNewOrderItems(this.currentPage, this.pageSize, this.currentFilter, this.currentCategoryId || undefined)
      .subscribe({
        next: (page: Page<NewOrderItem>) => {
          const newItems = page.content.map(item => ({
            ...item,
            pathImage: null
          }));
          if (this.currentPage === 0) {
            this.newOrderItems = newItems;
          } else {
            this.newOrderItems = [...this.newOrderItems, ...newItems];
          }
          this.filteredItems = [...this.newOrderItems];
          this.totalElements = page.totalElements;

          newItems.forEach(item => {
            this.getImage(item);
          });
        },
        error: (error) => { }
      });
  }

  private getImage(item: NewOrderItem): void {
    const idImage = item.isKit ? item.kit?.id : item.id;
    if (idImage) {
      this.ordersService.getImage(idImage, item.isKit)
        .subscribe({
          next: (imageBlob: Blob) => {
            if (imageBlob && imageBlob.size > 0) {
              const reader = new FileReader();
              reader.onload = () => {
                item.pathImage = reader.result as string;
              };
              reader.readAsDataURL(imageBlob);
            } else {
              item.pathImage = this.PLACEHOLDER_IMAGE;
            }
          },
          error: () => {
            item.pathImage = this.PLACEHOLDER_IMAGE;
          }
        });
    } else {
      item.pathImage = this.PLACEHOLDER_IMAGE;
    }
  }

  public loadMoreItems(): void {
    if (this.newOrderItems.length < this.totalElements) {
      this.currentPage++;
      this.loadNewOrderItems();
    }
  }

  loadOrderDetails(): void {
    if (!this.orderId) return;

    this.ordersService.getOrderItems(Number(this.orderId)).subscribe({
      next: (orderResponse: OrderResponse) => {
        this.setOriginalOrderFields(orderResponse);
        this.setFormFieldsFromOrder(orderResponse);
        if (this.cartItems.length === 0) {
          this.cartItems = (orderResponse.items || []).map(detail => ({
            ...detail.item,
            requestedQuantity: detail.requestedQuantity || 1,
            kit: detail.kit || null
          }));
        }
        this.counter = this.cartItems.reduce((sum: number, item: any) => sum + (item.requestedQuantity || 0), 0);
        this.updateBreadcrumbsLabel();
        this.loadNewOrderItems();
      },
      error: () => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            title: 'alert.information',
            message: 'orders-dashboard.loadOrderError',
            param: { orderId: this.orderId },
          },
        });
      }
    });
  }

  private setOriginalOrderFields(orderResponse: OrderResponse): void {
    this.originalProjectDescription = orderResponse.projectDescription || '';
    this.originalProjectType = orderResponse.projectType || '';
    this.originalRemarks = orderResponse.remarks || '';
    this.originalProjectCostCenter = orderResponse.costCenter || '';

    this.setLocalStorageIfAbsent('originalProjectDescription', this.originalProjectDescription);
    this.setLocalStorageIfAbsent('originalProjectType', this.originalProjectType);
    this.setLocalStorageIfAbsent('originalRemarks', this.originalRemarks);
    this.setLocalStorageIfAbsent('originalProjectCostCenter', this.originalProjectCostCenter);
  }

  private setFormFieldsFromOrder(orderResponse: OrderResponse): void {
    if (!localStorage.getItem('projectDescription')) {
      this.projectDescription = orderResponse.projectDescription || '';
      localStorage.setItem('projectDescription', this.projectDescription);
    }
    if (!localStorage.getItem('projectType')) {
      this.projectType = orderResponse.projectType || '';
      localStorage.setItem('projectType', this.projectType);
    }
    if (!localStorage.getItem('remarks')) {
      this.remarks = orderResponse.remarks || '';
      localStorage.setItem('remarks', this.remarks);
    }
  }

  private setLocalStorageIfAbsent(key: string, value: string): void {
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, value);
    }
  }

  private loadCartFromLocalStorage(): void {
    const storedCart = localStorage.getItem('cartItems');
    if (storedCart) {
      const items = JSON.parse(storedCart);
        if (Array.isArray(items)) {
          this.cartItems = items;
          this.counter = this.cartItems.reduce((sum: number, item: any) => sum + (item.requestedQuantity || 0), 0);
          if (this.breadcrumbsData.buttons) {
            this.breadcrumbsData.buttons[1].disabled = false;
            this.breadcrumbsData.buttons[1].label = this.counter > 0 ? `Ver Carrinho (${this.counter})` : 'Ver Carrinho';
          }
        }
    }
  }

  filterItems(): void {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = setTimeout(() => {
      this.currentFilter = this.searchQuery;
      this.currentCategoryId = this.selectedCategoryId;
      this.currentPage = 0;
      this.loadNewOrderItems();
    }, 300);
  }

  isInCart(item: any): boolean {
    return this.cartItems.some(ci => ci.id === item.id);
  }

  decreaseQuantity(item: any): void {
    if (item.requestedQuantity >= 2) {
      item.requestedQuantity--;
      this.counter = this.cartItems.reduce((sum: number, curr: any) => sum + curr.requestedQuantity, 0);
      this.updateBreadcrumbsLabel();
    }
  }

  increaseQuantity(item: any): void {
    item.requestedQuantity++;
    this.counter = this.cartItems.reduce((sum: number, curr: any) => sum + curr.requestedQuantity, 0);
    this.updateBreadcrumbsLabel();
  }

  get badgeDisplay(): string {
    return this.counter > 99 ? '99+' : this.counter.toString();
  }

  updateBreadcrumbsLabel(): void {
    if (this.counter > 0) {
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons.length > 0) {
        this.breadcrumbsData.buttons[1].label = `Ver Carrinho (${this.badgeDisplay})`;
        this.breadcrumbsData.buttons[1].disabled = false;
      }
    } else {
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons.length > 0) {
        this.breadcrumbsData.buttons[1].label = 'Ver Carrinho';
        this.breadcrumbsData.buttons[1].disabled = true;
      }
    }
  }

  addToCart(item: NewOrderItem): void {
    if (item.isKit && item.kit?.items?.length) {
      // Processa cada item do kit
      for (const kitItem of item.kit.items) {
        const existingItem = this.cartItems.find(ci => ci.id === kitItem.id);
        if (existingItem) {
          // Incrementa a quantidade e adiciona dados do kit se faltarem
          existingItem.requestedQuantity = (existingItem.requestedQuantity || 0) + 1;
          if (!existingItem.kit) {
            existingItem.kit = { id: item.kit.id, description: item.kit.description };
          }
        } else {
          // Inicializa a quantidade e associa o kit
          kitItem.requestedQuantity = 1;
          (kitItem as any).kit = { id: item.kit.id, description: item.kit.description };
          this.cartItems.push(kitItem);
        }
      }
    } else {
      const existingItem = this.cartItems.find(ci => ci.id === item.id);
      if (existingItem) {
        existingItem.requestedQuantity += 1;
      } else {
        item.requestedQuantity = 1;
        item.kit = null;
        this.cartItems.push(item);
      }
    }
    this.counter = this.cartItems.reduce((sum: number, curr: any) => sum + curr.requestedQuantity, 0);
    this.updateBreadcrumbsLabel();
    if (this.breadcrumbsData.buttons?.length) {
      this.breadcrumbsData.buttons[0].disabled = false;
    }
    if (this.drawer && !this.drawer.opened) {
      this.drawer.open();
    }
  }

  removeFromCart(item: any): void {
    item.requestedQuantity--;
    if (item.requestedQuantity <= 0) {
      this.cartItems = this.cartItems.filter(ci => ci.id !== item.id);
    }
    localStorage.setItem('cartItems', JSON.stringify(this.cartItems));
    localStorage.setItem('totalQuantity', this.cartItems.reduce((sum: number, curr: any) => sum + curr.requestedQuantity, 0).toString());
    this.counter = this.cartItems.reduce((sum: number, curr: any) => sum + curr.requestedQuantity, 0);
    this.updateBreadcrumbsLabel();
  }

  navigateToSummary(): void {
    localStorage.setItem('cartItems', JSON.stringify(this.cartItems));
    localStorage.setItem('totalQuantity', this.counter.toString());
    localStorage.setItem('projectType', this.projectType);
    localStorage.setItem('projectDescription', this.projectDescription);
    localStorage.setItem('remarks', this.remarks);
    this.router.navigate(['/new-order/confirm'],{
      queryParams: {
        orderId: this.orderId,
      },
    });
  }

  validateQuantity(cartItem: any): void {
    if (!cartItem.requestedQuantity || cartItem.requestedQuantity < 1) {
      cartItem.requestedQuantity = 1;
      this.updateCartLocalStorage();
    }
  }

  toggleKit(item: NewOrderItem): void {
    const index = this.expandedKits.indexOf(item.id);
    if (index > -1) {
      this.expandedKits.splice(index, 1);
    } else {
      this.expandedKits.push(item.id);
    }
  }

  isKitExpanded(item: NewOrderItem): boolean {
    return this.expandedKits.includes(item.id);
  }

  updateCartLocalStorage(): void {
    localStorage.setItem('cartItems', JSON.stringify(this.cartItems));
    const total = this.cartItems.reduce((sum: number, curr: any) => sum + (curr.requestedQuantity || 0), 0);
    localStorage.setItem('totalQuantity', total.toString());
    this.counter = total;
    this.updateBreadcrumbsLabel();
  }

  confirmDiscard(): void {
    const isEditing = localStorage.getItem('isEditingOrder') === 'true';
    const message = isEditing
      ? this.translate.instant('order-summary.discard-edit-changes-confirmation')
      : this.translate.instant('order-summary.discard-changes-confirmation');

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        message: message,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirm) {
        this.removeLocalStorage();
        this.router.navigate(['/orders-dashboard']);
      }
    });
  }

  removeLocalStorage() {
  this.localStorageService.clearAll();
  }

  private loadFormValuesFromLocalStorage(): void {
    const storedProjectDescription = localStorage.getItem('projectDescription');
    const storedProjectType = localStorage.getItem('projectType');
    const storedRemarks = localStorage.getItem('remarks');

    if (storedProjectDescription) {
      this.projectDescription = storedProjectDescription;
    }
    if (storedProjectType) {
      this.projectType = storedProjectType;
    }
    if (storedRemarks) {
      this.remarks = storedRemarks;
    }
  }
}
